/**
 * Per-mailbox usage for one page: `GET /domains/{domain}/accounts/{email}/usage`, read LAZILY and
 * separately from the list, as the API returns it.
 *
 * The service's guidance is to fetch usage for a detail view rather than for every row of a grid:
 * each read costs it two upstream round trips and nothing is cached. The grid keeps its storage
 * bar anyway (Figma draws one), so it asks only for the tiles that scroll into view, at most once
 * per mailbox, and holds the answer for the life of the page. The mailbox page asks once, on mount.
 *
 * The cache is a page's, not the app's: each route mounts its own (through `useMailboxes`), so
 * leaving the page drops it and coming back reads again. It is also the SCOPE's (the mock seed, or
 * the live brand and domain): a brand switch brings a new scope, and every answer for the old one is
 * dropped, including one still in flight. The read itself is the caller's (`read`): the mock, or
 * ows-mail, whose answers are never cached by the service either.
 */
import React from 'react';

import type { MailboxError, MailboxRecord, MailboxUsage } from '../../mock-data/mailbox-records';
import type { MailboxUsageQuery } from '../../mock-data/mailbox-usage';
import { takeMailboxError } from './errors';

/** One usage read, as the source answers it: `null` for a mailbox with none to read. */
export type MailboxUsageReader = (
  mailbox: Pick<MailboxRecord, 'email' | 'status'>,
  query: MailboxUsageQuery | undefined,
) => Promise<MailboxUsage | null>;

/** One mailbox's usage, as the page holds it. */
export type MailboxUsageRead =
  /** Not answered yet (or not asked yet: a tile below the fold). The UI shows a skeleton. */
  | { status: 'loading' }
  /** Answered. `usage` is `null` for a mailbox with none to read (a `deleted` one). */
  | { status: 'ready'; usage: MailboxUsage | null }
  /** The read failed. The UI shows the no-value marker and stays usable. `error` is the service's
   *  answer (its `message` is for the operator), `null` when the service did not answer. */
  | { status: 'failed'; error: MailboxError | null };

export type MailboxUsageCache = {
  /** What the page holds for this mailbox right now. */
  usageOf: (mailbox: Pick<MailboxRecord, 'email' | 'status' | 'createdAt'>) => MailboxUsageRead;
  /**
   * Ask for this mailbox's usage. A no-op when it was already asked for on this page (answered or
   * still in flight), so every caller may call it freely: one request per mailbox, ever. A
   * `deleted` mailbox is never asked about; it has none to read.
   */
  requestUsage: (mailbox: Pick<MailboxRecord, 'email' | 'status' | 'createdAt'>, query?: MailboxUsageQuery) => void;
};

/** What the answers belong to, compared by identity: a new scope drops them all. */
type Scope = unknown;

const LOADING: MailboxUsageRead = { status: 'loading' };
const NONE: MailboxUsageRead = { status: 'ready', usage: null };

/** The cache key. `createdAt` is in it so an address deleted and created again is read afresh:
 *  the provider starts a re-created mailbox at zero. */
const keyOf = (mailbox: Pick<MailboxRecord, 'email' | 'createdAt'>): string => `${mailbox.email}\n${mailbox.createdAt}`;

/** The mailbox's usage when it has been read, else `null`: for copy that names a figure only when
 *  one is known (the delete dialog's message count). */
export const readyUsage = (read: MailboxUsageRead): MailboxUsage | null =>
  read.status === 'ready' ? read.usage : null;

export const useMailboxUsage = (scope: Scope, read: MailboxUsageReader): MailboxUsageCache => {
  // Answers, tagged with the scope they belong to, so a brand switch reads as "nothing yet".
  const [answers, setAnswers] = React.useState<{ scope: Scope; byKey: Record<string, MailboxUsageRead> }>(() => ({
    scope,
    byKey: {},
  }));
  // What has been asked for, per scope: the one-in-flight rule. A ref, so a tile's effect that runs
  // twice (StrictMode) or on every re-render still sends one request.
  const asked = React.useRef<{ scope: Scope; keys: Set<string> }>({ scope, keys: new Set() });
  // The scope on screen, for an answer that lands after a brand switch.
  const current = React.useRef(scope);
  current.current = scope;

  const requestUsage = React.useCallback<MailboxUsageCache['requestUsage']>(
    (mailbox, query) => {
      if (mailbox.status === 'deleted') return;
      if (asked.current.scope !== scope) asked.current = { scope, keys: new Set() };
      const key = keyOf(mailbox);
      if (asked.current.keys.has(key)) return;
      asked.current.keys.add(key);
      const settle = (answer: MailboxUsageRead): void => {
        if (current.current !== scope) return;
        setAnswers((prev) => ({ scope, byKey: { ...(prev.scope === scope ? prev.byKey : {}), [key]: answer } }));
      };
      read(mailbox, query).then(
        (usage) => settle({ status: 'ready', usage }),
        (rejection: unknown) => settle({ status: 'failed', error: takeMailboxError(rejection) }),
      );
    },
    [scope, read],
  );

  const usageOf = React.useCallback<MailboxUsageCache['usageOf']>(
    (mailbox) => {
      if (mailbox.status === 'deleted') return NONE;
      if (answers.scope !== scope) return LOADING;
      return answers.byKey[keyOf(mailbox)] ?? LOADING;
    },
    [answers, scope],
  );

  return { usageOf, requestUsage };
};

export default useMailboxUsage;
